import Vue from 'vue';
import VueMq from 'vue-mq';
import { mapTileUrl, mapTileSettings } from './../../constants/map-constants';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-fullscreen';
import debounce from 'debounce';

Vue.use(VueMq, {
    breakpoints: {
        sm: 450,
        md: 1250,
        lg: Infinity,
    }
});

export default Vue.component('leaflet-map', {
    template: `
        <div id="map" class="servicepoints__map"><slot /></div>
    `,
    props: {
        mapCenter: Object
    },
    data: function () {
        return {
            currentMarkers: [],
            defaultCenter: [52.1326, 5.2913],
            zoomLevel: 8,
            zoomLevelSet: false,
        };
    },
    methods: {
        renderMap(location) {

            // initialize Leaflet map
            this.map = L.map('leafletmap', {
                center: L.latLng(location),
                zoomControl: false, // add through api with options
                zoom: this.zoomLevel,
                scrollWheelZoom: !this.$isEmbedded
            });

            // add zoom control with position prop
            L.control.zoom({
                position: 'bottomright'
            }).addTo(this.map);

            if (this.$isEmbedded) {
                // add fullscreen controll with position prop
                L.control.fullscreen({
                    position: 'bottomright'
                }).addTo(this.map);
            }

            // add mapbox tile layer 
            L.tileLayer(mapTileUrl, mapTileSettings).addTo(this.map);

            // bind move end event
            this.map.addEventListener('moveend', debounce(() => {
                this.$root.$emit('clearAddress');
                this.onMapChanged();
            }, 300));

            // bind zoom event
            this.map.addEventListener('zoomend', () => {
                this.$root.$emit('clearAddress');
                this.zoomLevel = this.map.getZoom();
            });

            // Pan map to center of popup
            this.map.on('popupopen', function(e) {
                var px = this.project(e.target._popup._latlng); // find the pixel location on the map where the popup anchor is
                px.y -= e.target._popup._container.clientHeight/2; // find the height of the popup container, divide by 2, subtract from the Y axis of marker location
                this.panTo(this.unproject(px),{animate: true}); // pan to new center
            });

            // layer
            this.layerGroup = L.layerGroup().addTo(this.map);
        },
        onMapChanged() {
            this.$emit('mapchanged', this.map.getBounds());
        },
        getMapCenter() {
            return this.map.getCenter();
        },
        showInfoWindow(servicePoint) {
            const servicePointAddressNumber = [servicePoint.address.number, servicePoint.address.suffix];
            const infoWindowBanner = document.getElementById('info-window-banner');

            return L.popup({
                offset: [0, -35],
                minWidth: 320
            })
                .setLatLng([servicePoint.lat, servicePoint.lon] )
                .setContent(function () {
                    var banner = document.getElementById('popupBanner').innerHTML;
                    return `<div class="row">
                      <div class="column small-12 leaflet-popup-content__title">
                        <h5 style="font-weight: bold;">${servicePoint.name}</h5>
                        <p style="margin: 0;">${servicePoint.address.street} ${servicePointAddressNumber.filter(Boolean).join(" ")}, ${servicePoint.address.postalCode} ${servicePoint.address.city}</p>
                        ${ servicePoint.municipalityServicePoint ? `<p class="disclaimer" style="margin: 0.5rem 0 1rem 0;">${window.translates.municipalityDisclaimer} ${servicePoint.municipalities.join(', ') }</p>` : '' }
                        ${ servicePoint.additionalInfo ? `<p class="disclaimer" style="margin: 0.5rem 0 1rem 0;">${servicePoint.additionalInfo}</p>` : '' }
                        <p style="margin: 0.5rem 0;"><strong>${window.translates.returnHere}</strong></p>
                      </div>
                    </div>
                    <div class="row">
                      <div class="column small-12 medium-6 leaflet-popup-content__list">
                        <ul class="list list--tick">
                            ${servicePoint.wasteTypes.filter(wt => wt.allowed).map(function (wasteType) {
                                return '<li>' + wasteType.title + '</li>'       
                            }).join('')}
                        </ul>
                      </div>
                      <div class="column small-12 medium-6 leaflet-popup-content__list">
                        <ul class="list list--cross">
                            ${servicePoint.wasteTypes.filter(wt => !wt.allowed).map(function (wasteType) {
                                return '<li>' + wasteType.title + '</li>'       
                            }).join('')}
                        </ul>
                      </div>
                    </div>
                    ${banner}
                    <div class="row">
                      <div class="column small-12 medium-6 leaflet-popup-content__button">
                        <a class="button button--xsmall button--primary" href="https://www.google.com/maps/dir//${servicePoint.lat},${servicePoint.lon}/" target="_blank">${window.translates.routeLocation}</a>
                      </div>
                      <div class="column small-12 medium-6 leaflet-popup-content__button">
                        <button class="button button--xsmall button--secondary" type="button" data-feedback>${window.translates.reportFeedback}</button>
                      </div>
                    </div>`;
                    })
                .openOn(this.map);
        },
        centerMap(location, zoomLevel) {
            // Set location
            if (!location || typeof location === 'undefined') {
                location = this.defaultCenter;
            }

            // Set zoom level
            if (zoomLevel || this.zoomLevelSet) {
                this.zoomLevel = zoomLevel;
            } else if (this.$mq === 'sm') {
                this.zoomLevel = 12;
            } else {
                this.zoomLevel = 16;
            }

            if (this.map === undefined) {
                this.renderMap(location);
            } else {
                this.map.setView(location, this.zoomLevel);
            }
        },
        setView(location, zoomLevel) {
            this.map.setView(location, zoomLevel);
        },
        setZoom(zoomLevel) {
            this.map.setZoom(zoomLevel);
        },
        clearMarkers() {
            // remove all the markers in one go
            this.layerGroup.clearLayers();
            this.resetCluster();
            this.currentMarkers = [];
        },
        addMarker(marker) {
            this.currentMarkers.push(marker);
        },
        resetCluster() {
            // marker cluster
            this.clusterGroup = L.markerClusterGroup({
                showCoverageOnHover: false,
                zoomToBoundsOnClick: true,
                iconCreateFunction: (cluster) => {

                    return L.divIcon({
                        html: cluster.getChildCount(),
                        className: 'map-icon map-icon--cluster',
                        iconSize: L.point(40, 40),
                        iconAnchor: [20, 40]
                    });
                }
            });
        },
        showMarkers(markers) {
            this.clearMarkers();

            if (markers !== null && markers.length > 0) {
                //markers.forEach(o => this.addMarker(o));
                markers.forEach(marker => {
                    this.clusterGroup.addLayer(marker);
                });

                //this.map.addLayer(this.clusterGroup);
                this.clusterGroup.addTo(this.layerGroup);
            }
        },
    },
    created() {
        if (this.$urlParameters && this.$urlParameters.has('zoomLevel')) {
            this.zoomLevel = parseInt(this.$urlParameters.get('zoomLevel'));
            this.zoomLevelSet = true;
        }
    },
    mounted() {
        // default center on the Netherlands
        this.renderMap(this.defaultCenter);
        this.resetCluster();
    }
});